import { useState } from "react";
import { api, type Me, type TenantRole } from "../api";
import { useAsync } from "../hooks";
import { fmt } from "../format";
import { useConfirmAction } from "./useConfirmAction";

interface Member {
  id: string;
  email: string;
  role: TenantRole;
  linked: boolean;
  /** 'invited' = they haven't accepted the workspace invitation yet; 'active' = joined. */
  status: "active" | "invited";
  isSelf: boolean;
  createdAt: string;
}

const ROLES: TenantRole[] = ["owner", "admin", "member", "viewer"];
const ROLE_HELP: Record<TenantRole, string> = {
  owner: "Everything, incl. team + billing",
  admin: "Manage scrapers, keys, team",
  member: "Create & run scrapers",
  viewer: "Read-only",
};

/**
 * Per-customer team (ROADMAP L4 RBAC). Everyone in the org can see the team; owners/admins can
 * invite, change roles, and remove. Server enforces the same rules — the disabling here is just UX.
 */
type SortKey = "email" | "role" | "status" | "since";

export function Team({ me }: { me: Me }) {
  const { data, loading, error, reload } = useAsync<Member[]>(() => api("/team"), []);
  const [email, setEmail] = useState("");
  const [inviteRole, setInviteRole] = useState<TenantRole>("member");
  const [busy, setBusy] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  // Adding a member is now a modal off an "Add member" button — no always-open invite box taking room.
  const [addOpen, setAddOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [sort, setSort] = useState<{ key: SortKey; dir: "asc" | "desc" }>({ key: "role", dir: "asc" });
  const confirmRemove = useConfirmAction();
  const confirmLeave = useConfirmAction();

  const canManage = me.admin || me.tenantRole === "owner" || me.tenantRole === "admin";
  // Only an owner (or operator break-glass) manages the owner role — an admin must not grant itself
  // owner or change an owner. Server enforces the same (teamGuard); this is just the matching UX.
  const isOwnerActor = me.admin || me.tenantRole === "owner";

  function openAdd() {
    setEmail("");
    setErr(null);
    setAddOpen(true);
  }

  async function invite() {
    const e = email.trim().toLowerCase();
    if (!e) return;
    setBusy("invite");
    setErr(null);
    try {
      await api("/team/invites", { method: "POST", body: JSON.stringify({ email: e, role: inviteRole }) });
      setEmail("");
      setAddOpen(false);
      reload();
    } catch (x) {
      setErr(x instanceof Error ? x.message : "failed to invite");
    } finally {
      setBusy(null);
    }
  }

  async function changeRole(m: Member, role: TenantRole) {
    setBusy(m.id);
    setErr(null);
    try {
      await api(`/team/${m.id}`, { method: "PATCH", body: JSON.stringify({ role }) });
      reload();
    } catch (x) {
      setErr(x instanceof Error ? x.message : "failed to change role");
    } finally {
      setBusy(null);
    }
  }

  function leave() {
    confirmLeave.ask({
      title: "Leave this workspace?",
      body: "You'll lose access to its scrapers and data. An owner can invite you back later.",
      confirmLabel: "Leave workspace",
      danger: true,
      run: async () => {
        await api("/team/me", { method: "DELETE" });
        // No longer a member of this org — reload from the root so identity + active tenant re-resolve.
        window.location.href = "/";
      },
    });
  }

  function remove(m: Member) {
    confirmRemove.ask({
      title: `Remove ${m.email} from the team?`,
      confirmLabel: "Remove",
      danger: true,
      run: async () => {
        await api(`/team/${m.id}`, { method: "DELETE" });
        reload();
      },
    });
  }

  if (loading && !data) return <div className="muted pad">Loading…</div>;
  if (error) return <div className="err pad">Error: {error}</div>;
  if (!data) return null;

  const members = data;
  const ownerCount = members.filter((m) => m.role === "owner").length;
  const meMember = members.find((m) => m.isSelf);
  // The last owner can't leave (would orphan the org) — they transfer ownership or delete the workspace.
  const meIsLastOwner = meMember?.role === "owner" && ownerCount <= 1;

  const q = query.trim().toLowerCase();
  const filtered = q
    ? members.filter((m) => m.email.toLowerCase().includes(q) || m.role.includes(q))
    : members;
  const sorted = [...filtered].sort((a, b) => {
    let c = 0;
    if (sort.key === "email") c = a.email.localeCompare(b.email);
    else if (sort.key === "role") c = ROLES.indexOf(a.role) - ROLES.indexOf(b.role);
    else if (sort.key === "status") c = Number(a.linked) - Number(b.linked);
    else c = Date.parse(a.createdAt) - Date.parse(b.createdAt);
    return sort.dir === "asc" ? c : -c;
  });
  const toggleSort = (key: SortKey) =>
    setSort((s) => (s.key === key ? { key, dir: s.dir === "asc" ? "desc" : "asc" } : { key, dir: "asc" }));
  const th = (key: SortKey, label: string) => (
    <th
      className="th-sort"
      aria-sort={sort.key === key ? (sort.dir === "asc" ? "ascending" : "descending") : undefined}
      onClick={() => toggleSort(key)}
    >
      {label}
      <span className="th-caret">{sort.key === key ? (sort.dir === "asc" ? " ▲" : " ▼") : ""}</span>
    </th>
  );

  return (
    <div className="stack">
      <div className="section-head">
        <h1>Team</h1>
        <span
          className="muted"
          title="Everyone in this workspace shares the same scrapers; a member's role sets what they can do."
        >
          Who can access this workspace.
        </span>
      </div>

      <section className="card">
        <div className="section-head">
          <h2>Members</h2>
          <div className="row-gap" style={{ gap: "var(--sp2)" }}>
            {meMember && (
              <button
                className="btn ghost"
                onClick={leave}
                disabled={meIsLastOwner || busy !== null}
                title={meIsLastOwner ? "You're the last owner — transfer ownership or delete the workspace first" : undefined}
              >
                Leave workspace
              </button>
            )}
            {canManage && (
              <button className="btn seam" onClick={openAdd}>
                Add member
              </button>
            )}
          </div>
        </div>

        {!addOpen && err && <div className="err mt">{err}</div>}

        <div className="row-gap" style={{ marginTop: "var(--sp3)", gap: "var(--sp3)" }}>
          <input
            type="search"
            placeholder="Search members…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            style={{ flex: 1, minWidth: 0 }}
          />
          <span className="muted sm" style={{ whiteSpace: "nowrap" }}>
            {sorted.length === members.length
              ? `${members.length} member${members.length === 1 ? "" : "s"}`
              : `${sorted.length} of ${members.length}`}
          </span>
        </div>

        <table style={{ marginTop: "var(--sp3)" }}>
          <thead>
            <tr>
              {th("email", "Email")}
              {th("role", "Role")}
              {th("status", "Status")}
              {th("since", "Since")}
              {canManage && <th />}
            </tr>
          </thead>
          <tbody>
            {sorted.map((m) => {
              const lastOwner = m.role === "owner" && ownerCount <= 1;
              return (
                <tr key={m.id}>
                  <td>
                    {m.email}
                    {m.isSelf && <span className="muted sm"> (you)</span>}
                  </td>
                  <td>
                    {canManage ? (
                      <select
                        value={m.role}
                        disabled={busy === m.id || lastOwner || (!isOwnerActor && m.role === "owner")}
                        title={
                          lastOwner
                            ? "Cannot demote the last owner"
                            : !isOwnerActor && m.role === "owner"
                              ? "Only an owner can change an owner"
                              : undefined
                        }
                        onChange={(e) => void changeRole(m, e.target.value as TenantRole)}
                      >
                        {ROLES.filter((r) => r !== "owner" || isOwnerActor || m.role === "owner").map((r) => (
                          <option key={r} value={r}>
                            {r}
                          </option>
                        ))}
                      </select>
                    ) : (
                      m.role
                    )}
                  </td>
                  <td>
                    <span
                      className={m.status === "active" ? "pill" : "pill hold"}
                      title={
                        m.status === "active"
                          ? undefined
                          : "Invitation sent — pending until they accept it"
                      }
                    >
                      {m.status === "active" ? "active" : "invited"}
                    </span>
                  </td>
                  <td className="muted sm">{fmt(m.createdAt)}</td>
                  {canManage && (
                    <td style={{ textAlign: "right" }}>
                      <button
                        className="btn danger"
                        disabled={busy === m.id || m.isSelf || lastOwner}
                        title={m.isSelf ? "You cannot remove yourself" : lastOwner ? "Cannot remove the last owner" : undefined}
                        onClick={() => void remove(m)}
                      >
                        {busy === m.id ? "…" : "Remove"}
                      </button>
                    </td>
                  )}
                </tr>
              );
            })}
            {sorted.length === 0 && (
              <tr>
                <td colSpan={canManage ? 5 : 4} className="muted pad">
                  {q ? "No members match your search." : "No members yet."}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </section>

      {addOpen && (
        <div className="modal-backdrop" onClick={busy === "invite" ? undefined : () => setAddOpen(false)}>
          <div
            className="modal"
            role="dialog"
            aria-modal="true"
            onClick={(e) => e.stopPropagation()}
            style={{ width: "min(440px, 100%)" }}
          >
            <div className="section-head">
              <h2>Add member</h2>
            </div>
            {err && <div className="err mt">{err}</div>}
            <div style={{ display: "flex", flexDirection: "column", gap: "var(--sp1)", marginTop: "var(--sp4)" }}>
              <span className="muted sm">Email</span>
              <input
                type="email"
                autoFocus
                placeholder="teammate@company.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && void invite()}
              />
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: "var(--sp1)", marginTop: "var(--sp3)" }}>
              <span className="muted sm">Role</span>
              <select value={inviteRole} onChange={(e) => setInviteRole(e.target.value as TenantRole)}>
                {ROLES.map((r) => (
                  <option key={r} value={r}>
                    {r}
                  </option>
                ))}
              </select>
              <span className="muted sm">{ROLE_HELP[inviteRole]}</span>
            </div>
            <div className="row-gap" style={{ marginTop: "var(--sp5)" }}>
              <div style={{ flex: 1 }} />
              <button className="btn ghost" onClick={() => setAddOpen(false)} disabled={busy === "invite"}>
                Cancel
              </button>
              <button className="btn seam" onClick={() => void invite()} disabled={busy === "invite" || !email.trim()}>
                {busy === "invite" ? "Inviting…" : "Send invite"}
              </button>
            </div>
          </div>
        </div>
      )}
      {confirmRemove.modal}
      {confirmLeave.modal}
    </div>
  );
}
